import { useEffect, useRef, useState } from "react";

import { isTaskActive } from "@/shared/lib/taskStatus";
import { videoService, type VideoTaskDto } from "../api/videoService";
import { errorText } from "@/shared/lib/apiErrors";

/**
 * ★在途轮询间隔。
 *
 * - `PENDING`(排队,等 worker CAS 认领)→ **1.5s**:马上就要翻 RUNNING,轮快点好让界面跟上
 * - `RUNNING`(provider 正在出片,以**分钟**计)→ **5s**:轮快了纯属打服务端
 *
 * 上一版这里是 `new Set(["RENDERING","DOWNLOADING","POST_SAFETY"]).has(t.status)` —— 后端
 * **从不产**这三个态,`.has()` 恒 false → **interval 恒 1500ms**,文档写的退避是死代码。
 * 而「漏 RUNNING 导致轮询提前停」被修好之后,这条死分支的代价从「无所谓」变成「整个出片过程
 * (数分钟)每 1.5s 打一次 GET」。这里按**真实词汇**恢复它本来的意图。
 *
 * ⚠️ 这条没能被「收窄 MediaTaskStatus 类型」顶出来:`Set<string>.has(status: string)` 收
 * `MediaTaskStatus` 完全合法,tsc 不报错。**类型收窄对 `Set.has()` 这类残留结构上是瞎的** ——
 * 别把「类型改窄了 = 死态清干净了」当成完整性保证(这条是复审抓的,不是 tsc 抓的)。
 */
export function pollIntervalMs(status: string): number {
  return status === "RUNNING" ? 5000 : 1500;
}

/**
 * 视频任务详情轮询。
 *
 * - 在途(PENDING/RUNNING):间隔见 {@link pollIntervalMs}
 * - SUCCESS/PARTIAL/FAILED/BLOCKED/CANCELLED:停止
 *
 * <p>★2026-07-16 修:原本地 ACTIVE_STATUSES **漏了 `RUNNING`**(只列 PENDING + 一串 rebuild 后端
 * 根本不产的 legacy 态)→ 任务被 worker 认领(PENDING→RUNNING)的那一刻轮询就停,详情页冻在
 * 「处理中」直到手动刷新。改走 {@link isTaskActive} 单一真相源(列表页同款问题 2026-07-15 已修,
 * 当时没搜同款,这份和 VideoTaskDetailPage 那份又漏了一轮)。
 */
export function useVideoTaskPolling(taskId: number | null) {
  const [task, setTask] = useState<VideoTaskDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 手动刷新(删镜/仅保存等非渲染动作后任务已终态、轮询停了,bump 这个重取一次,不闪屏)。
  const [refreshTick, setRefreshTick] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 仅 taskId 变化时清空(避免详情页闪现上一个任务的数据);refresh 不清空。
  useEffect(() => {
    setTask(null);
    setError(null);
  }, [taskId]);

  useEffect(() => {
    if (!taskId) {
      return;
    }
    let cancelled = false;

    const fetchOnce = async (): Promise<VideoTaskDto | null> => {
      try {
        const t = await videoService.getTask(taskId);
        if (cancelled) return null;
        setTask(t);
        setError(null);
        return t;
      } catch (e: unknown) {
        if (cancelled) return null;
        setError(errorText(e));
        return null;
      }
    };

    const loop = async () => {
      const t = await fetchOnce();
      if (cancelled) return;
      if (!t || !isTaskActive(t.status)) {
        return; // terminal — stop
      }
      timerRef.current = setTimeout(loop, pollIntervalMs(t.status));
    };

    void loop();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [taskId, refreshTick]);

  return { task, error, refresh: () => setRefreshTick((x) => x + 1) };
}
